// src/features/imprint/stores/useNewImprintStore.js
/**
 * Store for "Start New Imprint" form (Step 2 - Customize Product)
 * FIXED: Added explicit imprintKey parameter to prevent cross-imprint contamination
 */

import { defineStore } from 'pinia';
import { useImprintStore } from './useImprintStore';
import { useSelectionStore } from './useSelectionStore';
import { useAppStore } from './useAppStore';

// Import fee calculator utilities
import {
    calculateAllFees,
    getUniqueColorsCount,
    hasPantoneColor,
    hasUploadedImage,
    getUniqueCustomColorsCount
} from '../utils/feeCalculator.js';

// SessionStorage persistence constants
const STORAGE_KEY = 'new_imprint_state_v1';
const STORAGE_VERSION = 1;
const STORAGE_TTL = 30 * 60 * 1000; // 30 minutes

export const useNewImprintStore = defineStore('newImprint', {
    state: () => ({
        // Current imprint key (for multiple imprints support)
        currentImprintKey: 0,

        // Imprints data - array to support multiple imprints
        imprints: [
            {
                // Selected location
                selectedLocation: {
                    sku: null,
                    title: null,
                    imprintLines: 5 // Default max lines
                },

                // ✅ NEW: Separate state for each imprint method (Screen Print, Embroidery, DTG, etc.)
                // Each method preserves its own textLines, colors, images when switching
                methodsData: {},

                // ✅ NEW: Track which imprint method is currently active
                // Example: 'SCRE' for Screen Print, 'EMBR' for Embroidery
                activeMethodSku: null,

                // ✅ NEW: Separate state for each location (preserves textLines when switching locations)
                // Structure: { 'LOC_SKU': { textLines: [...], nextLineId: N } }
                // Example: { 'FRC': { textLines: [line1, line2], nextLineId: 3 } }
                locationsData: {},

                // LEGACY: Old fields kept for backwards compatibility
                // These are now computed from methodsData[activeMethodSku]
                // Message type: 'text' | 'image' | 'text_image'
                messageType: 'text',

                // Text lines with colors - Initialize with 1 line (will be adjusted when location is selected)
                textLines: [
                    {
                        id: 0,
                        randID: Date.now(),
                        type: 'message',
                        selectedColor: null,
                        selectedColorObj: null,
                        customColor: false,
                        message: '',
                        showColorPicker: false
                    }
                ],

                // Image data (for Image/Text+Image types)
                imageData: null,

                // Special instructions
                specialInstructions: '',

                // Max lines allowed for current location
                maxLines: 5,

                // Validation state
                errors: {},

                // Next line ID counter
                nextLineId: 1, // Start from 1 since we have 1 initial line

                // Apply to All state (per imprint)
                applyToAll: {
                    active: false,
                    sourceLineId: null,
                    sourceLineRandID: null,
                    color: null,
                    colorObj: null,
                    customColor: false
                },

                // Max colors tracking (per imprint)
                maxColorsTracking: {
                    uniqueColors: [], // Array of color SKUs
                    maxColors: null, // Max allowed from product config
                    isMaxReached: false
                },

                // Custom Pantone inputs (UI state per imprint)
                customPantoneInputs: {},

                // Pending color selection (for Add Colour button flow)
                pendingColorSelection: {
                    lineId: null,
                    color: null,
                    colorObj: null,
                    customColor: false
                },

                // Saved Pantone colors (for reuse)
                savedPantoneColors: [],

                // Saved image line data (preserved when switching to 'text' mode)
                savedImageLineData: null
            }
        ]
    }),

    getters: {
        /**
         * Get current imprint data
         */
        currentImprint(state) {
            return state.imprints[state.currentImprintKey] || null;
        },

        /**
         * Get current text lines
         */
        currentTextLines(state) {
            const imprint = state.imprints[state.currentImprintKey];
            return imprint?.textLines || [];
        },

        /**
         * Get current location
         */
        currentLocation(state) {
            const imprint = state.imprints[state.currentImprintKey];
            return imprint?.selectedLocation || null;
        },

        /**
         * Get current message type
         */
        currentMessageType(state) {
            const imprint = state.imprints[state.currentImprintKey];
            return imprint?.messageType || 'text';
        },

        /**
         * Get max lines for current location
         */
        currentMaxLines(state) {
            const imprint = state.imprints[state.currentImprintKey];
            return imprint?.maxLines || 5;
        },

        // Note: canAddLine and canRemoveLine removed - all lines auto-shown on desktop based on maxLines

        /**
         * Get available locations from main imprint store
         * FIXED: Now includes imprint_lines from fullJson
         */
        availableLocations() {
            const imprintStore = useImprintStore();
            const baseLocations = imprintStore.availableLocations || [];
            
            console.log('[DEBUG availableLocations] START');
            console.log('[DEBUG availableLocations] baseLocations count:', baseLocations.length);
            if (baseLocations.length > 0) {
                console.log('[DEBUG availableLocations] First base location:', baseLocations[0]);
            }
            
            // Get imprint_lines from fullJson for each location
            const fullJson = imprintStore.fullJson;
            const selectedSku = imprintStore.selectedImprintMethod?.sku;
            
            console.log('[DEBUG availableLocations] selectedSku:', selectedSku);
            console.log('[DEBUG availableLocations] Has fullJson:', !!fullJson);
            console.log('[DEBUG availableLocations] Has configurable_array:', !!fullJson?.configurable_array);
            console.log('[DEBUG availableLocations] Has selected SKU in array:', !!fullJson?.configurable_array?.[selectedSku]);
            
            if (!fullJson || !selectedSku || !fullJson.configurable_array?.[selectedSku]) {
                console.warn('[useNewImprintStore] Cannot enrich locations - missing fullJson or selectedSku');
                console.warn('[DEBUG availableLocations] Returning BASE locations (NOT enriched)');
                return baseLocations;
            }
            
            const configData = fullJson.configurable_array[selectedSku];
            console.log('[DEBUG availableLocations] configData keys:', Object.keys(configData));
            console.log('[DEBUG availableLocations] Has locations directly:', !!configData.locations);
            
            // CRITICAL FIX: Locations are in configData.locations, NOT in configurable_2_data.locations!
            const locationsFromJson = configData.locations || [];
            
            console.log('[DEBUG availableLocations] locationsFromJson count:', locationsFromJson.length);
            console.log('[DEBUG availableLocations] All JSON location SKUs:', locationsFromJson.map(l => l.sku));
            if (locationsFromJson.length > 0) {
                console.log('[DEBUG availableLocations] First JSON location:', locationsFromJson[0]);
                console.log('[DEBUG availableLocations] First JSON location imprint_lines:', locationsFromJson[0]?.imprint_lines);
            }
            console.log('[DEBUG availableLocations] All base location SKUs:', baseLocations.map(l => l.sku));
            
            // Map baseLocations and add imprint_lines from fullJson
            const enrichedLocations = baseLocations.map(loc => {
                const jsonLocation = locationsFromJson.find(jloc => jloc.sku === loc.sku);
                
                // CRITICAL: Check if imprint_lines already exists in baseLocation
                const imprintLines = loc.imprint_lines || jsonLocation?.imprint_lines || 5;
                
                console.log(`[DEBUG availableLocations] Mapping location ${loc.sku}:`, {
                    baseLocation: loc,
                    'loc.imprint_lines (already exists?)': loc.imprint_lines,
                    jsonLocation: jsonLocation,
                    'jsonLocation?.imprint_lines': jsonLocation?.imprint_lines,
                    'FINAL imprintLines': imprintLines
                });
                return {
                    ...loc,
                    imprint_lines: imprintLines  // Use existing or fallback
                };
            });
            
            console.log('[DEBUG availableLocations] FINAL enriched locations:', enrichedLocations);
            console.log('[DEBUG availableLocations] First enriched location:', enrichedLocations[0]);
            console.log('[useNewImprintStore] Enriched locations with imprint_lines:', enrichedLocations.length);
            return enrichedLocations;
        },

        /**
         * Get available colors from main imprint store
         */
        availableColors() {
            const imprintStore = useImprintStore();
            return imprintStore.availableColors || [];
        },

        /**
         * Get custom color fee from product data
         */
        customColorFee() {
            const imprintStore = useImprintStore();
            const configData = imprintStore.currentConfigurable2Data;
            return configData?.imprint_data?.custom_colour_fee || 0;
        },

        /**
         * Get unique colors count for current imprint
         */
        currentUniqueColorsCount(state) {
            const imprint = state.imprints[state.currentImprintKey];
            return imprint?.maxColorsTracking?.uniqueColors?.length || 0;
        },

        /**
         * Check if max colors reached for current imprint
         */
        isMaxColorsReached(state) {
            const imprint = state.imprints[state.currentImprintKey];
            return imprint?.maxColorsTracking?.isMaxReached || false;
        },

        /**
         * Get unique colors array for current imprint
         */
        currentUniqueColors(state) {
            const imprint = state.imprints[state.currentImprintKey];
            return imprint?.maxColorsTracking?.uniqueColors || [];
        },

        /**
         * Check if has image line in current imprint
         */
        hasImageLine(state) {
            const imprint = state.imprints[state.currentImprintKey];
            if (!imprint) return false;
            
            return imprint.textLines.some(line => line.type === 'image');
        },

        /**
         * Get validation errors for current imprint
         */
        currentErrors(state) {
            const imprint = state.imprints[state.currentImprintKey];
            return imprint?.errors || {};
        },

        /**
         * Get all currently selected location SKUs across all imprints
         * @returns {string[]} Array of location SKUs
         */
        usedLocationSkus(state) {
            return state.imprints
                .map(imprint => imprint.selectedLocation?.sku)
                .filter(Boolean); // Remove null/undefined
        },

        /**
         * Check if a location SKU is already used in any imprint
         * @param {Object} state
         * @returns {function(string): boolean}
         */
        isLocationUsed: (state) => (sku) => {
            if (!sku) return false;
            
            const usedSkus = state.imprints
                .map(imprint => imprint.selectedLocation?.sku)
                .filter(Boolean);
            
            return usedSkus.includes(sku);
        },

        /**
         * Check if a location SKU is used in OTHER imprints (not current)
         * @param {Object} state
         * @returns {function(string, number|null): boolean}
         */
        isLocationUsedInOtherImprints: (state) => (sku, currentImprintKey = null) => {
            if (!sku) return false;
            
            const key = currentImprintKey !== null ? currentImprintKey : state.currentImprintKey;
            
            return state.imprints.some((imprint, index) => {
                // Skip current imprint
                if (index === key) return false;
                
                return imprint.selectedLocation?.sku === sku;
            });
        },

        /**
         * Get available locations excluding already used ones
         * Useful for dropdowns when adding new imprints
         * @returns {Array} Filtered locations array
         */
        availableLocationsForNewImprint(state) {
            const imprintStore = useImprintStore();
            const allLocations = imprintStore.availableLocations || [];
            const usedSkus = state.usedLocationSkus;
            
            // Filter out locations that are already used
            return allLocations.filter(loc => !usedSkus.includes(loc.sku));
        }
    },

    actions: {
        /**
         * Get max_color for a given imprint method from product's fullJson config.
         * Each imprint method (SCRE, EMBR, DTG, etc.) can have its own max_color limit.
         * @param {string} methodSku
         * @returns {number|null} max colors allowed, or null if not configured
         */
        getMaxColorsForMethod(methodSku) {
            const imprintStore = useImprintStore();
            const maxColor = imprintStore.fullJson?.configurable_array?.[methodSku]?.max_color;
            return maxColor ? Number(maxColor) : null;
        },

        /**
         * Initialize store from product data
         */
        initializeFromProductData() {
            const imprintStore = useImprintStore();
            const configData = imprintStore.currentConfigurable2Data;
            
            if (!configData) {
                console.warn('[useNewImprintStore] No configurable_2_data available');
                return;
            }

            const imprint = this.imprints[this.currentImprintKey];
            
            // Set max colors from product config
            const selectedSku = imprintStore.selectedImprintMethod?.sku;
            
            const maxColors = this.getMaxColorsForMethod(selectedSku);
            if (maxColors !== null) {
                imprint.maxColorsTracking.maxColors = maxColors;
            }

            // Set default location if available
            const locations = imprintStore.availableLocations;
            if (locations && locations.length > 0 && !imprint.selectedLocation.sku) {
                this.setLocation(locations[0]);
            }

            console.log('[useNewImprintStore] Initialized from product data', {
                maxColors: imprint.maxColorsTracking.maxColors,
                locations: locations?.length,
                colors: imprintStore.availableColors?.length
            });
        },

        /**
         * ✅ NEW: Create empty method data structure
         * Used when initializing a new imprint method (Screen Print, Embroidery, etc.)
         * Each method gets its own isolated state for textLines, colors, images
         * 
         * @returns {Object} Empty method data structure
         */
        createEmptyMethodData() {
            return {
                messageType: 'text',
                textLines: [
                    {
                        id: 0,
                        randID: Date.now(),
                        type: 'message',
                        selectedColor: null,
                        selectedColorObj: null,
                        customColor: false,
                        message: '',
                        showColorPicker: false
                    }
                ],
                // imageData is NOT stored per-method — it lives on imprint directly (shared across methods)
                specialInstructions: '',
                maxLines: 5,
                errors: {},
                nextLineId: 1,
                applyToAll: {
                    active: false,
                    sourceLineId: null,
                    sourceLineRandID: null,
                    color: null,
                    colorObj: null,
                    customColor: false
                },
                maxColorsTracking: {
                    uniqueColors: [],
                    maxColors: null,
                    isMaxReached: false
                },
                customPantoneInputs: {},
                pendingColorSelection: {
                    lineId: null,
                    color: null,
                    colorObj: null,
                    customColor: false
                },
                savedPantoneColors: []
            };
        },

        /**
         * ✅ NEW: Set active imprint method (Screen Print, Embroidery, DTG, etc.)
         * Switches between imprint methods while preserving each method's data
         * Auto-creates methodsData if it doesn't exist
         * 
         * IMPORTANT: This is called when user selects a different imprint method
         * Example: User switches from Screen Print (SCRE) to Embroidery (EMBR)
         * - Screen Print data is preserved in methodsData['SCRE']
         * - Embroidery gets clean slate in methodsData['EMBR']
         * - Switching back to Screen Print restores original data
         * 
         * @param {string} methodSku - Method SKU (e.g., 'SCRE', 'EMBR', 'DTG')
         * @param {number|null} imprintKey - Imprint index (defaults to current)
         */
        setActiveImprintMethod(methodSku, imprintKey = null) {
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            
            if (!imprint) {
                console.warn('[useNewImprintStore] setActiveImprintMethod: Invalid imprint key', key);
                return;
            }
            
            const previousMethod = imprint.activeMethodSku;

            console.log('%c[METHOD-SWITCH] ▶ setActiveImprintMethod START', 'color: orange; font-weight: bold', {
                from: previousMethod,
                to: methodSku,
                imprintKey: key,
                'imprint.messageType (before)': imprint.messageType,
                'imprint.imageData (before)': imprint.imageData ? '✅ HAS DATA' : '❌ NULL',
                'methodsData keys': Object.keys(imprint.methodsData),
                'methodsData[from].imageData': previousMethod ? (imprint.methodsData[previousMethod]?.imageData ? '✅ HAS DATA' : '❌ NULL') : 'N/A',
                'methodsData[from].messageType': previousMethod ? imprint.methodsData[previousMethod]?.messageType : 'N/A',
            });

            // Note: imageData is NOT saved/restored per-method.
            // It is shared across all methods of the same imprint position.
            // The uploaded file belongs to the imprint position, not to a specific method.
            
            // MIGRATION: If this is first time setting active method, migrate existing data
            if (!previousMethod && !imprint.methodsData[methodSku]) {
                console.log('[useNewImprintStore] First time setting method - migrating existing data');
                
                // CRITICAL FIX: Use maxLines from location if available, not from imprint.maxLines (which might be default 5)
                const targetMaxLines = imprint.selectedLocation?.imprintLines || imprint.maxLines || 5;
                
                console.log('[useNewImprintStore] MIGRATION maxLines:', {
                    'imprint.maxLines': imprint.maxLines,
                    'imprint.selectedLocation?.imprintLines': imprint.selectedLocation?.imprintLines,
                    'targetMaxLines (final)': targetMaxLines
                });
                
                // Migrate existing data to methodsData[methodSku]
                // Note: imageData is NOT migrated — it stays on imprint directly (shared)
                imprint.methodsData[methodSku] = {
                    messageType: imprint.messageType || 'text',
                    textLines: imprint.textLines?.length > 0 ? [...imprint.textLines] : [this.createEmptyMethodData().textLines[0]],
                    specialInstructions: imprint.specialInstructions || '',
                    maxLines: targetMaxLines,  // ✅ Use maxLines from location, not default
                    errors: imprint.errors || {},
                    nextLineId: imprint.nextLineId || 1,
                    applyToAll: imprint.applyToAll || this.createEmptyMethodData().applyToAll,
                    maxColorsTracking: imprint.maxColorsTracking || this.createEmptyMethodData().maxColorsTracking,
                    customPantoneInputs: imprint.customPantoneInputs || {},
                    pendingColorSelection: imprint.pendingColorSelection || this.createEmptyMethodData().pendingColorSelection,
                    savedPantoneColors: imprint.savedPantoneColors || []
                };
            }
            
            // Set new active method
            imprint.activeMethodSku = methodSku;
            
            // Initialize methodsData for new method if it doesn't exist
            if (!imprint.methodsData[methodSku]) {
                console.log('[useNewImprintStore] Creating new method data for:', methodSku);
                imprint.methodsData[methodSku] = this.createEmptyMethodData();

                const methodMaxColors = this.getMaxColorsForMethod(methodSku);
                if (methodMaxColors !== null) {
                    imprint.methodsData[methodSku].maxColorsTracking.maxColors = methodMaxColors;
                }
                // Copy maxLines from location (same for all methods)
                if (imprint.selectedLocation?.imprintLines) {
                    const targetMaxLines = imprint.selectedLocation.imprintLines;
                    imprint.methodsData[methodSku].maxLines = targetMaxLines;
                    
                    // CRITICAL FIX: Auto-adjust textLines to match location maxLines
                    // createEmptyMethodData() creates 1 line by default, but location might require more/less
                    const newData = imprint.methodsData[methodSku];
                    const currentLines = newData.textLines.filter(l => l.type === 'message').length;
                    
                    console.log('[useNewImprintStore] Auto-adjusting lines for new method:', {
                        method: methodSku,
                        currentLines,
                        targetMaxLines
                    });
                    
                    if (currentLines < targetMaxLines) {
                        // Add missing lines to reach maxLines
                        const linesToAdd = targetMaxLines - currentLines;
                        for (let i = 0; i < linesToAdd; i++) {
                            newData.textLines.push({
                                id: newData.nextLineId++,
                                randID: Date.now() + i,
                                type: 'message',
                                selectedColor: null,
                                selectedColorObj: null,
                                customColor: false,
                                message: '',
                                showColorPicker: false
                            });
                        }
                        console.log('[useNewImprintStore] Added', linesToAdd, 'lines for new method');
                    } else if (currentLines > targetMaxLines) {
                        // Remove excess lines (shouldn't happen with default 1 line, but defensive coding)
                        const linesToRemove = currentLines - targetMaxLines;
                        let removed = 0;
                        for (let i = newData.textLines.length - 1; i >= 0 && removed < linesToRemove; i--) {
                            if (newData.textLines[i].type === 'message') {
                                newData.textLines.splice(i, 1);
                                removed++;
                            }
                        }
                        console.log('[useNewImprintStore] Removed', removed, 'lines for new method');
                    }
                }
            }
            
            // Update legacy fields to point to active method data (for backwards compatibility)
            const activeData = imprint.methodsData[methodSku];
            console.log('%c[METHOD-SWITCH] Loading activeData for method:', 'color: darkorange', methodSku, {
                'activeData.messageType': activeData.messageType,
                // imageData is shared (not per-method), so we show imprint.imageData
                'imprint.imageData (shared, unchanged)': imprint.imageData ? '✅ HAS DATA' : '❌ NULL',
            });
            imprint.messageType = activeData.messageType;
            imprint.textLines = activeData.textLines;
            // imageData is intentionally NOT restored from methodsData — it is shared across all methods
            imprint.specialInstructions = activeData.specialInstructions;
            imprint.maxLines = activeData.maxLines;
            imprint.errors = activeData.errors;
            // CRITICAL: nextLineId must NEVER decrease — it is a monotonic counter.
            // methodsData[methodSku].nextLineId is a primitive and is NOT reactively synced
            // when imprint.nextLineId is incremented elsewhere (e.g. in setMessageType).
            // Restoring a stale (lower) value causes new lines to receive ids that are
            // already taken by existing lines, leading to id collisions and color pickers
            // opening under the wrong line.
            imprint.nextLineId = Math.max(imprint.nextLineId, activeData.nextLineId);
            imprint.applyToAll = activeData.applyToAll;
            imprint.maxColorsTracking = activeData.maxColorsTracking;
            imprint.customPantoneInputs = activeData.customPantoneInputs;
            // Reset picker state when switching methods to prevent stale pickers.
            imprint.pendingColorSelection = { lineId: null, color: null, colorObj: null, customColor: false };
            imprint.textLines.forEach(line => { line.showColorPicker = false; });
            imprint.savedPantoneColors = activeData.savedPantoneColors;
            
            // ✅ CRITICAL FIX: Sync maxLines from current location (it's the same for all methods)
            // This ensures maxLines always matches the selected location when switching between methods
            // Without this, methodsData[methodSku].maxLines can become stale if location was changed after method creation
            if (imprint.selectedLocation?.imprintLines) {
                const locationMaxLines = imprint.selectedLocation.imprintLines;
                console.log('[useNewImprintStore] Syncing maxLines from location:', {
                    method: methodSku,
                    'activeData.maxLines (before)': activeData.maxLines,
                    'imprint.maxLines (before)': imprint.maxLines,
                    'location.imprintLines': locationMaxLines,
                    'will update to': locationMaxLines
                });
                activeData.maxLines = locationMaxLines;
                imprint.maxLines = locationMaxLines;
            }
            
            console.log('%c[METHOD-SWITCH] ✅ setActiveImprintMethod END', 'color: green; font-weight: bold', {
                from: previousMethod,
                to: methodSku,
                'imprint.messageType (after)': imprint.messageType,
                'imprint.imageData (after)': imprint.imageData ? '✅ HAS DATA' : '❌ NULL',
                'methodsData[to].messageType': imprint.methodsData[methodSku]?.messageType,
                'methodsData[to].imageData': imprint.methodsData[methodSku]?.imageData ? '✅ HAS DATA' : '❌ NULL',
                'methodsData[from].imageData (saved)': previousMethod ? (imprint.methodsData[previousMethod]?.imageData ? '✅ HAS DATA' : '❌ NULL') : 'N/A',
                textLinesCount: activeData.textLines?.length || 0
            });
        },

        /**
         * Validate location selection for duplicate check
         * @param {string} locationSku - Location SKU to validate
         * @param {number|null} imprintKey - Optional imprint key to exclude from check (defaults to currentImprintKey)
         * @returns {Object} { valid: boolean, error: string|null }
         */
        validateLocationSelection(locationSku, imprintKey = null) {
            if (!locationSku) {
                return { 
                    valid: false, 
                    error: 'Please select a location' 
                };
            }
            
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            
            // Check if location is used in OTHER imprints (not current)
            const isUsedInOther = this.isLocationUsedInOtherImprints(locationSku, key);
            
            if (isUsedInOther) {
                return { 
                    valid: false, 
                    error: 'This location is already used in another imprint. Please select a different location.' 
                };
            }
            
            return { valid: true, error: null };
        },

        /**
         * Set selected location
         * @param {ImprintLocation} location
         * @param {number|null} imprintKey - Optional imprint key (defaults to currentImprintKey)
         */
        setLocation(location, imprintKey = null) {
            console.log('[DEBUG setLocation] START - Location:', location.title, 'imprint_lines:', location.imprint_lines);
            
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            if (!imprint) {
                console.log('[DEBUG setLocation] ERROR - Imprint not found for key:', key);
                return;
            }

            const newMaxLines = location.imprint_lines || 5;
            console.log('[DEBUG setLocation] newMaxLines:', newMaxLines, 'current textLines count:', imprint.textLines.length);
            
            // ✅ NEW: Save current location's textLines before switching (if location was selected)
            const previousLocationSku = imprint.selectedLocation?.sku;
            if (previousLocationSku && previousLocationSku !== location.sku) {
                console.log('[setLocation] 💾 Saving textLines for previous location:', previousLocationSku);
                imprint.locationsData[previousLocationSku] = {
                    textLines: [...imprint.textLines],  // Deep copy
                    nextLineId: imprint.nextLineId
                };
                console.log('[setLocation] Saved', imprint.textLines.length, 'lines for', previousLocationSku);
            }

            imprint.selectedLocation = {
                sku: location.sku,
                title: location.title,
                imprintLines: newMaxLines
            };
            
            imprint.maxLines = newMaxLines;
            
            // ✅ NEW: Restore textLines from saved location data (if exists)
            const savedLocationData = imprint.locationsData[location.sku];
            if (savedLocationData) {
                console.log('[setLocation] 📚 Restoring textLines for location:', location.sku);
                imprint.textLines = [...savedLocationData.textLines];  // Deep copy
                imprint.nextLineId = savedLocationData.nextLineId;
                
                // Recalculate unique colors after restoration
                this.recalculateUniqueColors(key);
                
                console.log('[setLocation] ✅ Restored', imprint.textLines.length, 'lines from saved data');
                console.log('[setLocation] Restored lines:', imprint.textLines.map(l => ({
                    id: l.id,
                    type: l.type,
                    message: l.message?.substring(0, 20),
                    hasColor: !!l.selectedColor
                })));
                
                // Clear location error if exists
                if (imprint.errors.location) {
                    delete imprint.errors.location;
                }
                
                // Clear maxLines error if exists
                if (imprint.errors.maxLines) {
                    delete imprint.errors.maxLines;
                }
                
                // ✅ CRITICAL: Sync maxLines to active method's data (if method exists)
                if (imprint.activeMethodSku && imprint.methodsData[imprint.activeMethodSku]) {
                    console.log('[useNewImprintStore] Syncing maxLines to active method:', {
                        method: imprint.activeMethodSku,
                        'methodsData.maxLines (before)': imprint.methodsData[imprint.activeMethodSku].maxLines,
                        'newMaxLines': newMaxLines
                    });
                    imprint.methodsData[imprint.activeMethodSku].maxLines = newMaxLines;
                }
                
                console.log('[useNewImprintStore] Location restored:', location.title, 'Lines:', imprint.textLines.length);
                return;  // ✅ EXIT EARLY - don't auto-adjust, we restored saved data
            }

            // No saved data - continue with auto-adjust logic
            console.log('[setLocation] No saved data for location:', location.sku, '- will auto-adjust lines');

            // Adjust text lines count to match new maxLines
            const messageLines = imprint.textLines.filter(l => l.type === 'message');
            const currentCount = messageLines.length;
            console.log('[DEBUG setLocation] BEFORE auto-adjust - messageLines:', currentCount, 'maxLines:', newMaxLines);

            if (currentCount > newMaxLines) {
                console.log('[DEBUG setLocation] REMOVING LINES - Need to remove:', currentCount - newMaxLines);
                // Remove excess lines (from end)
                const linesToRemove = currentCount - newMaxLines;
                console.log('[useNewImprintStore] Removing', linesToRemove, 'excess lines. Current:', currentCount, 'Max:', newMaxLines);
                
                // Remove only message lines, keep image lines
                let removed = 0;
                for (let i = imprint.textLines.length - 1; i >= 0 && removed < linesToRemove; i--) {
                    if (imprint.textLines[i].type === 'message') {
                        imprint.textLines.splice(i, 1);
                        removed++;
                    }
                }
                
                // Recalculate unique colors after removal
                this.recalculateUniqueColors(key);
                console.log('[DEBUG setLocation] AFTER REMOVAL - textLines count:', imprint.textLines.length);
            } else if (currentCount < newMaxLines) {
                console.log('[DEBUG setLocation] ADDING LINES - Need to add:', newMaxLines - currentCount);
                // Add missing lines (to reach maxLines)
                const linesToAdd = newMaxLines - currentCount;
                console.log('[useNewImprintStore] Adding', linesToAdd, 'lines to reach max. Current:', currentCount, 'Max:', newMaxLines);
                
                // Find index where to insert (before image lines if they exist)
                const imageLineIndex = imprint.textLines.findIndex(l => l.type === 'image');
                const insertIndex = imageLineIndex !== -1 ? imageLineIndex : imprint.textLines.length;
                
                for (let i = 0; i < linesToAdd; i++) {
                    const newLine = {
                        id: imprint.nextLineId++,
                        randID: Date.now() + i,
                        type: 'message',
                        selectedColor: imprint.applyToAll.active ? imprint.applyToAll.color : null,
                        selectedColorObj: imprint.applyToAll.active ? imprint.applyToAll.colorObj : null,
                        customColor: imprint.applyToAll.active ? imprint.applyToAll.customColor : false,
                        message: '',
                        showColorPicker: false
                    };
                    imprint.textLines.splice(insertIndex + i, 0, newLine);
                }
                console.log('[DEBUG setLocation] AFTER ADDING - textLines count:', imprint.textLines.length);
            } else {
                console.log('[DEBUG setLocation] NO CHANGE - Already correct count');
            }

            console.log('[DEBUG setLocation] FINAL - textLines:', imprint.textLines.length, 'messageLines:', imprint.textLines.filter(l => l.type === 'message').length);

            // Clear location error if exists
            if (imprint.errors.location) {
                delete imprint.errors.location;
            }
            
            // Clear maxLines error if exists (after adjusting lines)
            if (imprint.errors.maxLines) {
                delete imprint.errors.maxLines;
            }
            
            // ✅ CRITICAL: Sync maxLines to active method's data (if method exists)
            // This ensures methodsData[activeMethodSku].maxLines stays in sync with location
            // Without this, changing location AFTER selecting method leaves stale maxLines in methodsData
            if (imprint.activeMethodSku && imprint.methodsData[imprint.activeMethodSku]) {
                console.log('[useNewImprintStore] Syncing maxLines to active method:', {
                    method: imprint.activeMethodSku,
                    'methodsData.maxLines (before)': imprint.methodsData[imprint.activeMethodSku].maxLines,
                    'newMaxLines': newMaxLines
                });
                imprint.methodsData[imprint.activeMethodSku].maxLines = newMaxLines;
            }

            console.log('[useNewImprintStore] Location set:', location.title, 'Max lines:', imprint.maxLines, 'Current lines:', imprint.textLines.filter(l => l.type === 'message').length, 'ImprintKey:', key);
        },

        /**
         * Set message type (text/image/text_image)
         * @param {string} type
         * @param {number|null} imprintKey - Optional imprint key
         */
        setMessageType(type, imprintKey = null) {
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            if (!imprint) return;

            // Close all open color pickers and clear pending selection before switching tabs.
            // Without this, showColorPicker=true stays on lines after tab switch,
            // causing the picker to appear immediately when returning to that tab.
            // Also, pendingColorSelection.lineId would reference a stale id after
            // the image line is removed/re-created with a new id, disabling "Add Colour".
            imprint.textLines.forEach(line => { line.showColorPicker = false; });
            imprint.pendingColorSelection = { lineId: null, color: null, colorObj: null, customColor: false };

            const oldType = imprint.messageType;
            console.log('%c[MSG-TYPE] setMessageType called', 'color: blue; font-weight: bold', {
                from: oldType,
                to: type,
                imprintKey: key,
                activeMethodSku: imprint.activeMethodSku,
            });
            console.trace('[MSG-TYPE] call stack');
            imprint.messageType = type;
            
            // Sync messageType to active method data to prevent stale state when switching methods
            if (imprint.activeMethodSku && imprint.methodsData[imprint.activeMethodSku]) {
                imprint.methodsData[imprint.activeMethodSku].messageType = type;
            }

            // Handle image line addition/removal
            const existingImageLine = imprint.textLines.find(line => line.type === 'image');
            
            if ((type === 'image' || type === 'text_image') && oldType === 'text') {
                // Switching FROM text TO image/text_image
                // Add image line if doesn't exist
                if (!existingImageLine) {
                    // Check if we have saved image line data to restore
                    if (imprint.savedImageLineData) {
                        // Restore saved image line with its color
                        const restoredImageLine = {
                            ...imprint.savedImageLineData,
                            id: imprint.nextLineId++,
                            randID: Date.now(),
                            showColorPicker: false
                        };
                        imprint.textLines.push(restoredImageLine);
                        console.log('[useNewImprintStore] Restored saved image line data:', restoredImageLine);
                    } else {
                        // Create new image line
                        const imageLine = {
                            id: imprint.nextLineId++,
                            randID: Date.now(),
                            type: 'image',
                            selectedColor: imprint.applyToAll.active ? imprint.applyToAll.color : null,
                            selectedColorObj: imprint.applyToAll.active ? imprint.applyToAll.colorObj : null,
                            customColor: imprint.applyToAll.active ? imprint.applyToAll.customColor : false,
                            message: '',
                            showColorPicker: false
                        };
                        imprint.textLines.push(imageLine);
                    }
                }
            } else if (type === 'text' && oldType !== 'text') {
                // Switching FROM image/text_image TO text
                // Save image line data before removing (preserve color selection)
                if (existingImageLine) {
                    imprint.savedImageLineData = {
                        type: existingImageLine.type,
                        selectedColor: existingImageLine.selectedColor,
                        selectedColorObj: existingImageLine.selectedColorObj,
                        customColor: existingImageLine.customColor,
                        message: existingImageLine.message
                    };
                    console.log('[useNewImprintStore] Saved image line data:', imprint.savedImageLineData);
                }
                // Remove image line from textLines (but keep imageData and savedImageLineData)
                imprint.textLines = imprint.textLines.filter(line => line.type !== 'image');
            }

            console.log('[useNewImprintStore] Message type set:', type, 'ImprintKey:', key);
        },

        // Note: addTextLine() and removeTextLine() removed
        // Lines are automatically managed by setLocation() based on maxLines
        // All lines are shown on desktop - no manual add/remove needed

        /**
         * Update line text
         * @param {number} lineId
         * @param {string} text
         * @param {number|null} imprintKey - Optional imprint key
         */
        updateLineText(lineId, text, imprintKey = null) {
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            if (!imprint) return;

            const line = imprint.textLines.find(l => l.id === lineId);
            if (line) {
                line.message = text;
                
                // Clear message error if text is not empty
                if (text && text.trim()) {
                    delete imprint.errors[`line${lineId}_message`];
                }
            }
        },

        /**
         * Set color for line
         * @param {number} lineId
         * @param {Object} color - Color object with {sku, desc, file_name}
         * @param {number|null} imprintKey - Optional imprint key
         */
        setLineColor(lineId, color, imprintKey = null) {
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            if (!imprint) return;

            const line = imprint.textLines.find(l => l.id === lineId);
            if (!line) return;

            const tracking = imprint.maxColorsTracking;
            const oldColorSku = line.selectedColorObj?.sku || null;
            const newColorSku = color.sku;

            // No actual change
            if (oldColorSku === newColorSku) {
                return;
            }

            // Colours currently used by lines OTHER than this one (state before this change)
            const othersColors = new Set();
            imprint.textLines.forEach(l => {
                if (l.id === lineId) return;
                if (l.selectedColorObj && l.selectedColorObj.sku) {
                    othersColors.add(l.selectedColorObj.sku);
                }
            });

            // What the total distinct colour count WOULD be if we just assigned
            // the new colour to this line normally (no cascade)
            const prospectiveColors = new Set(othersColors);
            prospectiveColors.add(newColorSku);

            const maxColors = tracking.maxColors;
            const wouldExceedLimit = maxColors !== null && prospectiveColors.size > maxColors;

            if (wouldExceedLimit && oldColorSku) {
                imprint.textLines.forEach(l => {
                    if (l.selectedColorObj && l.selectedColorObj.sku === oldColorSku) {
                        l.selectedColor = color.file_name;
                        l.selectedColorObj = color;
                        l.customColor = false;
                        delete imprint.errors[`line${l.id}_color`];
                    }
                });
                console.log('[useNewImprintStore] Cascaded colour rename:', oldColorSku, '->', newColorSku, 'ImprintKey:', key);
            } else if (wouldExceedLimit && !oldColorSku) {
                    return;
            } else {
                // Normal assign - stays within (or under) the limit
                line.selectedColor = color.file_name;
                line.selectedColorObj = color;
                line.customColor = false;
                delete imprint.errors[`line${lineId}_color`];
            }

            // Update unique colors
            this.recalculateUniqueColors(key);

            // Apply to all if active and this is source line
            if (imprint.applyToAll.active && imprint.applyToAll.sourceLineId === lineId) {
                this.applyColorToAllLines(color, false, key);
            }

            console.log('[useNewImprintStore] Line color set:', lineId, color.desc, 'ImprintKey:', key);
        },

        /**
         * Set custom/Pantone color for line
         * @param {number} lineId
         * @param {string} colorName - Pantone name
         * @param {number|null} imprintKey - Optional imprint key
         */
        setLineCustomColor(lineId, colorName, imprintKey = null) {
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            if (!imprint) return;

            const line = imprint.textLines.find(l => l.id === lineId);
            if (!line) return;

            line.selectedColor = colorName;
            line.selectedColorObj = null;
            line.customColor = true;

            // Clear color error
            delete imprint.errors[`line${lineId}_color`];

            // Apply to all if active and this is source line
            if (imprint.applyToAll.active && imprint.applyToAll.sourceLineId === lineId) {
                this.applyColorToAllLines(colorName, true, key);
            }

            console.log('[useNewImprintStore] Line custom color set:', lineId, colorName, 'ImprintKey:', key);
        },

        /**
         * Select color as pending (not applied yet - waiting for Add Colour button)
         * @param {number} lineId
         * @param {Object|string} color - Color object or Pantone name
         * @param {boolean} isCustom - Is custom/Pantone color
         * @param {number|null} imprintKey - Optional imprint key
         */
        selectPendingColor(lineId, color, isCustom, imprintKey = null) {
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            if (!imprint) return;

            // Store pending color selection
            imprint.pendingColorSelection = {
                lineId: lineId,
                color: isCustom ? color : color.file_name,
                colorObj: isCustom ? null : color,
                customColor: isCustom
            };

            console.log('[useNewImprintStore] Pending color selected:', lineId, isCustom ? color : color.desc, 'ImprintKey:', key);
        },

        /**
         * Apply pending color to line (triggered by Add Colour button)
         * @param {number} lineId
         * @param {number|null} imprintKey - Optional imprint key
         */
        applyPendingColor(lineId, imprintKey = null) {
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            if (!imprint) return;

            const pending = imprint.pendingColorSelection;
            if (!pending || !pending.color || pending.lineId !== lineId) {
                console.warn('[useNewImprintStore] No pending color to apply for line:', lineId);
                return;
            }

            // Apply the pending color
            if (pending.customColor) {
                this.setLineCustomColor(lineId, pending.color, key);
            } else {
                this.setLineColor(lineId, pending.colorObj, key);
            }

            // Clear pending selection
            imprint.pendingColorSelection = {
                lineId: null,
                color: null,
                colorObj: null,
                customColor: false
            };

            console.log('[useNewImprintStore] Pending color applied to line:', lineId, 'ImprintKey:', key);
        },

        /**
         * Add Pantone color to saved list
         * @param {string} pantoneValue - Pantone name
         * @param {number|null} imprintKey - Optional imprint key
         */
        addSavedPantoneColor(pantoneValue, imprintKey = null) {
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            if (!imprint) return;

            // Check if already exists
            if (imprint.savedPantoneColors.includes(pantoneValue)) {
                console.log('[useNewImprintStore] Pantone color already saved:', pantoneValue);
                return;
            }

            // Add to saved list
            imprint.savedPantoneColors.push(pantoneValue);
            console.log('[useNewImprintStore] Pantone color saved:', pantoneValue, 'Total:', imprint.savedPantoneColors.length);
        },

        /**
         * Always open color picker for line (no toggle - used when clicking/focusing text input)
         * Closes all other open pickers for cleaner UX.
         * @param {number} lineId
         * @param {number|null} imprintKey - Optional imprint key
         */
        openColorPicker(lineId, imprintKey = null) {
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            if (!imprint) return;

            const line = imprint.textLines.find(l => l.id === lineId);
            if (!line) return;

            // Close all other open pickers first
            imprint.textLines.forEach(l => {
                if (l.id !== lineId) l.showColorPicker = false;
            });

            // Always open (never close) when called from text input
            line.showColorPicker = true;
        },

        /**
         * Toggle color picker visibility for line
         * @param {number} lineId
         * @param {number|null} imprintKey - Optional imprint key
         */
        toggleColorPicker(lineId, imprintKey = null) {
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            
            console.log('[useNewImprintStore] toggleColorPicker called:', {
                lineId,
                imprintKey: key,
                imprintFound: !!imprint,
                textLinesCount: imprint?.textLines?.length,
                textLineIds: imprint?.textLines?.map(l => ({ id: l.id, type: l.type }))
            });
            
            if (!imprint) {
                console.error('[useNewImprintStore] toggleColorPicker: imprint not found for key', key);
                return;
            }

            const line = imprint.textLines.find(l => l.id === lineId);
            console.log('[useNewImprintStore] toggleColorPicker: line found:', !!line, 'id searched:', lineId);
            
            if (line) {
                const isOpening = !line.showColorPicker;
                line.showColorPicker = isOpening;
                console.log('[useNewImprintStore] toggleColorPicker: showColorPicker set to', isOpening);
                
                // Clear pending selection when closing
                if (!isOpening && imprint.pendingColorSelection.lineId === lineId) {
                    imprint.pendingColorSelection = {
                        lineId: null,
                        color: null,
                        colorObj: null,
                        customColor: false
                    };
                }
            } else {
                console.error('[useNewImprintStore] toggleColorPicker: LINE NOT FOUND! id:', lineId, 
                    'available ids:', imprint.textLines.map(l => l.id));
            }
        },

        /**
         * Force-open colour picker for a specific line.
         * Closes other message-line pickers so only one is visible at a time.
         * Used when user focuses a text input — no toggle, only opens.
         * @param {number} lineId
         * @param {number|null} imprintKey
         */
        openColorPicker(lineId, imprintKey = null) {
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            if (!imprint) return;

            const line = imprint.textLines.find(l => l.id === lineId);
            if (!line) return;

            // Close other message-line pickers (show one at a time for text lines)
            imprint.textLines.forEach(l => {
                if (l.type === 'message' && l.id !== lineId && l.showColorPicker) {
                    l.showColorPicker = false;
                    // Clear pending selection that belonged to the closing line
                    if (imprint.pendingColorSelection.lineId === l.id) {
                        imprint.pendingColorSelection = {
                            lineId: null,
                            color: null,
                            colorObj: null,
                            customColor: false
                        };
                    }
                }
            });

            // Open the target line's picker
            line.showColorPicker = true;

            console.log('[useNewImprintStore] openColorPicker:', lineId, 'ImprintKey:', key);
        },

        /**
         * Toggle Apply to All
         * @param {number} sourceLineId - Line ID to use as source
         * @param {number|null} imprintKey - Optional imprint key (CRITICAL: prevents cross-imprint contamination)
         */
        toggleApplyToAll(sourceLineId, imprintKey = null) {
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            if (!imprint) return;

            const sourceLine = imprint.textLines.find(l => l.id === sourceLineId);
            if (!sourceLine) return;

            if (!imprint.applyToAll.active) {
                // Activate
                imprint.applyToAll.active = true;
                imprint.applyToAll.sourceLineId = sourceLineId;
                imprint.applyToAll.sourceLineRandID = sourceLine.randID;
                imprint.applyToAll.color = sourceLine.selectedColor;
                imprint.applyToAll.colorObj = sourceLine.selectedColorObj;
                imprint.applyToAll.customColor = sourceLine.customColor;

                // Apply color to all lines
                if (sourceLine.selectedColor) {
                    this.applyColorToAllLines(
                        sourceLine.customColor ? sourceLine.selectedColor : sourceLine.selectedColorObj,
                        sourceLine.customColor,
                        key
                    );
                }

                console.log('[useNewImprintStore] Apply to All activated:', sourceLineId, 'ImprintKey:', key);
            } else {
                // Deactivate
                imprint.applyToAll.active = false;
                imprint.applyToAll.sourceLineId = null;
                imprint.applyToAll.sourceLineRandID = null;
                imprint.applyToAll.color = null;
                imprint.applyToAll.colorObj = null;
                imprint.applyToAll.customColor = false;

                // Remove colors from other lines
                imprint.textLines.forEach(line => {
                    if (line.id !== sourceLineId) {
                        line.selectedColor = null;
                        line.selectedColorObj = null;
                        line.customColor = false;
                    }
                });

                // Recalculate unique colors
                this.recalculateUniqueColors(key);

                console.log('[useNewImprintStore] Apply to All deactivated. ImprintKey:', key);
            }
        },

        /**
         * Apply color to all lines (except source)
         * @param {Object|string} color - Color object or Pantone name
         * @param {boolean} isCustom - Is custom/Pantone color
         * @param {number|null} imprintKey - Optional imprint key
         */
        applyColorToAllLines(color, isCustom, imprintKey = null) {
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            if (!imprint) return;

            imprint.textLines.forEach(line => {
                if (line.id !== imprint.applyToAll.sourceLineId) {
                    if (isCustom) {
                        line.selectedColor = color;
                        line.selectedColorObj = null;
                        line.customColor = true;
                    } else {
                        line.selectedColor = color.file_name;
                        line.selectedColorObj = color;
                        line.customColor = false;
                    }
                }
            });

            // Recalculate unique colors (only source line color should remain)
            this.recalculateUniqueColors(key);
        },

        /**
         * Recalculate unique colors array
         * @param {number|null} imprintKey - Optional imprint key
         */
        recalculateUniqueColors(imprintKey = null) {
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            if (!imprint) return;

            const tracking = imprint.maxColorsTracking;
            const uniqueSkus = new Set();

            imprint.textLines.forEach(line => {
                if (line.selectedColorObj && line.selectedColorObj.sku) {
                    uniqueSkus.add(line.selectedColorObj.sku);
                }
                // Note: custom colors don't count toward max colors
            });

            tracking.uniqueColors = Array.from(uniqueSkus);
            tracking.isMaxReached = tracking.maxColors !== null && 
                                   tracking.uniqueColors.length >= tracking.maxColors;

            console.log('[useNewImprintStore] Unique colors:', tracking.uniqueColors.length, '/', tracking.maxColors, 'ImprintKey:', key);
        },

        /**
         * Update unique colors after line removal
         * @param {Object} line - Line being removed
         * @param {number|null} imprintKey - Optional imprint key
         */
        updateUniqueColorsAfterRemoval(line, imprintKey = null) {
            if (!line.selectedColorObj || !line.selectedColorObj.sku) return;

            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            const colorSku = line.selectedColorObj.sku;

            // Check if any other line uses this color
            const otherLineWithSameColor = imprint.textLines.find(
                l => l.id !== line.id && l.selectedColorObj?.sku === colorSku
            );

            if (!otherLineWithSameColor) {
                // Remove from unique colors
                const tracking = imprint.maxColorsTracking;
                tracking.uniqueColors = tracking.uniqueColors.filter(sku => sku !== colorSku);
                tracking.isMaxReached = tracking.maxColors !== null && 
                                       tracking.uniqueColors.length >= tracking.maxColors;
            }
        },

        /**
         * Set special instructions
         * @param {string} text
         * @param {number|null} imprintKey - Optional imprint key
         */
        setSpecialInstructions(text, imprintKey = null) {
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            if (imprint) {
                imprint.specialInstructions = text;
            }
        },

        /**
         * Set image data
         * @param {Object} data
         * @param {number|null} imprintKey - Optional imprint key
         */
        setImageData(data, imprintKey = null) {
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            if (imprint) {
                // imageData lives on imprint directly — shared across all methods for this imprint position
                imprint.imageData = data;
                console.log('[IMAGE-DEBUG] setImageData called:', {
                    imprintKey: key,
                    hasData: !!data,
                    activeMethodSku: imprint.activeMethodSku,
                });
            }
        },

        /**
         * Validate current imprint
         * @param {number|null} imprintKey - Optional imprint key
         * @returns {boolean} - True if valid
         */
        validate(imprintKey = null) {
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            if (!imprint) return false;

            console.log('[VALIDATE] Starting validation for imprintKey:', key);
            console.log('[VALIDATE] Message type:', imprint.messageType);
            console.log('[VALIDATE] Text lines count:', imprint.textLines.length);

            imprint.errors = {};

            // ✅ ENVELOP PRODUCTS: Skip all validation (no location, no imprint message)
            const appStore = useAppStore();
            if (appStore.isEnvelopProduct) {
                console.log('[VALIDATE] Envelop product - skipping all validation');
                return true;
            }

            // Validate location
            if (!imprint.selectedLocation.sku) {
                imprint.errors.location = 'Please select a location';
            }

            // Validate max lines (check if user exceeded limit after changing location)
            const messageLines = imprint.textLines.filter(l => l.type === 'message');
            const maxLines = imprint.maxLines || 5;
            
            if (messageLines.length > maxLines) {
                imprint.errors.maxLines = `This Imprint Location supports up to ${maxLines} ${maxLines === 1 ? 'line' : 'lines'} of imprint.`;
                console.log('[VALIDATE] Max lines exceeded:', messageLines.length, '>', maxLines);
            }

            // Validate based on message type
            if (imprint.messageType === 'text' || imprint.messageType === 'text_image') {
                // Validate text lines
                const messageLines = imprint.textLines.filter(l => l.type === 'message');
                console.log('[VALIDATE] Message lines:', messageLines.length);
                
                messageLines.forEach((line, index) => {
                    const hasText = line.message && line.message.trim().length > 0;
                    const hasColor = !!line.selectedColor;
                    console.log(`[VALIDATE] Line ${index+1} (id=${line.id}): text="${line.message}", hasText=${hasText}, hasColor=${hasColor}`);
                    
                    // Line 1 (index 0) is required - must have both text and color
                    if (index === 0) {
                        if (!line.selectedColor) {
                            imprint.errors[`line${line.id}_color`] = 'Please select a color';
                            console.log(`[VALIDATE] Added error: line${line.id}_color`);
                        }
                        if (!line.message || !line.message.trim()) {
                            imprint.errors[`line${line.id}_message`] = 'Please enter text';
                            console.log(`[VALIDATE] Added error: line${line.id}_message`);
                        }
                    } else {
                        // Lines 2-5 optional — validate only if partially filled
                        // If text entered but no colour → colour is required (even on optional lines)
                        if (hasText && !hasColor) {
                            imprint.errors[`line${line.id}_color`] = 'Please select a color';
                            console.log(`[VALIDATE] Added error: line${line.id}_color (hasText but no color)`);
                        }
                    }
                });

                // For Text & Image, validate image upload and color
                if (imprint.messageType === 'text_image') {
                    if (!imprint.imageData) {
                        imprint.errors.image = 'Please upload an image';
                    }
                    // CRITICAL: Image color required for Text & Image
                    const imageLine = imprint.textLines.find(l => l.type === 'image');
                    if (imageLine && !imageLine.selectedColor) {
                        imprint.errors.imageColor = 'Please select image color';
                    }
                }
            } else if (imprint.messageType === 'image') {
                // Image Only: validate image presence and image colour
                if (!imprint.imageData) {
                    imprint.errors.image = 'Please upload an image';
                }
                // Image colour is required (shown in Review section too)
                const imageLine = imprint.textLines.find(l => l.type === 'image');
                if (imageLine && !imageLine.selectedColor) {
                    imprint.errors.imageColor = 'Please select image color';
                }
            }

            console.log('[VALIDATE] Total errors:', Object.keys(imprint.errors).length, imprint.errors);
            return Object.keys(imprint.errors).length === 0;
        },

        /**
         * Get imprint data for export/submission
         * @param {number|null} imprintKey - Optional imprint key
         * @returns {Object}
         */
        getImprintData(imprintKey = null) {
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            if (!imprint) return null;

            return {
                location: imprint.selectedLocation,
                messageType: imprint.messageType,
                textLines: imprint.textLines.map(line => ({
                    id: line.id,
                    randID: line.randID,
                    type: line.type,
                    message: line.message,
                    selectedColor: line.selectedColor,
                    selectedColorObj: line.selectedColorObj,
                    customColor: line.customColor
                })),
                imageData: imprint.imageData,
                specialInstructions: imprint.specialInstructions,
                applyToAll: {
                    active: imprint.applyToAll.active,
                    sourceLineId: imprint.applyToAll.sourceLineId
                }
            };
        },

        /**
         * Reset current imprint to initial state
         * @param {number|null} imprintKey - Optional imprint key
         */
        resetCurrentImprint(imprintKey = null) {
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];
            if (!imprint) return;

            imprint.selectedLocation = {
                sku: null,
                title: null,
                imprintLines: 5
            };
            imprint.messageType = 'text';
            imprint.textLines = [
                {
                    id: 0,
                    randID: Date.now(),
                    type: 'message',
                    selectedColor: null,
                    selectedColorObj: null,
                    customColor: false,
                    message: '',
                    showColorPicker: false
                }
            ];
            imprint.imageData = null;
            imprint.specialInstructions = '';
            imprint.maxLines = 5; // Will be adjusted when location is selected
            imprint.errors = {};
            imprint.nextLineId = 1; // Start from 1 since we reset to 1 line

            // Reset apply to all
            imprint.applyToAll = {
                active: false,
                sourceLineId: null,
                sourceLineRandID: null,
                color: null,
                colorObj: null,
                customColor: false
            };

            // Reset max colors tracking
            imprint.maxColorsTracking = {
                uniqueColors: [],
                maxColors: null,
                isMaxReached: false
            };

            // Reset custom Pantone inputs
            imprint.customPantoneInputs = {};

            // Reset pending color selection
            imprint.pendingColorSelection = {
                lineId: null,
                color: null,
                colorObj: null,
                customColor: false
            };

            // Reset saved Pantone colors
            imprint.savedPantoneColors = [];
            
            // ✅ NEW: Reset locations data
            imprint.locationsData = {};

            console.log('[useNewImprintStore] Current imprint reset. ImprintKey:', key);
        },

        /**
         * Add new imprint (for multiple imprints support)
         * FIXED: Copy maxLines from first imprint if it has a selected location
         */
        addNewImprint() {
            const newIndex = this.imprints.length;
            const timestamp = Date.now();
            
            // ✅ CRITICAL FIX: Copy maxLines from first imprint's location if available
            // This ensures all imprints start with the correct line count
            const firstImprint = this.imprints[0];
            
            // Try multiple sources for maxLines (in priority order):
            // 1. methodsData[activeMethodSku].maxLines (most reliable)
            // 2. selectedLocation.imprintLines (from location)
            // 3. maxLines field (fallback)
            // 4. Default to 5
            const initialMaxLines = (
                firstImprint?.methodsData?.[firstImprint.activeMethodSku]?.maxLines ||
                firstImprint?.selectedLocation?.imprintLines ||
                firstImprint?.maxLines ||
                5
            );
            
            console.log('[useNewImprintStore] addNewImprint - copying maxLines from first imprint:', {
                firstImprintHasLocation: !!firstImprint?.selectedLocation?.sku,
                firstImprintMaxLines: firstImprint?.selectedLocation?.imprintLines,
                initialMaxLines
            });
            
            const newImprint = {
                selectedLocation: {
                    sku: null,
                    title: null,
                    imprintLines: initialMaxLines  // ✅ Use maxLines from first imprint
                },
                
                // ✅ CRITICAL FIX: Add methodsData and activeMethodSku fields
                // Without these, setActiveImprintMethod() cannot save data properly
                methodsData: {},
                activeMethodSku: null,
                
                // ✅ NEW: Locations data (empty for new imprint)
                locationsData: {},
                
                messageType: 'text',
                // Initialize with correct number of lines based on first imprint's location
                textLines: [],  // Will be populated below
                imageData: null,
                specialInstructions: '',
                maxLines: initialMaxLines,  // ✅ Use maxLines from first imprint
                errors: {},
                nextLineId: 0,  // Will be incremented as lines are added
                applyToAll: {
                    active: false,
                    sourceLineId: null,
                    sourceLineRandID: null,
                    color: null,
                    colorObj: null,
                    customColor: false
                },
                maxColorsTracking: {
                    uniqueColors: [],
                    maxColors: null,
                    isMaxReached: false
                },
                customPantoneInputs: {},
                pendingColorSelection: {
                    lineId: null,
                    color: null,
                    colorObj: null,
                    customColor: false
                },
                savedPantoneColors: []
            };
            
            // ✅ CRITICAL FIX: Pre-populate textLines with correct count (initialMaxLines)
            // This ensures second imprint starts with same line count as first
            for (let i = 0; i < initialMaxLines; i++) {
                newImprint.textLines.push({
                    id: newImprint.nextLineId++,
                    randID: timestamp + i,
                    type: 'message',
                    selectedColor: null,
                    selectedColorObj: null,
                    customColor: false,
                    message: '',
                    showColorPicker: false
                });
            }
            
            console.log('[useNewImprintStore] addNewImprint - created textLines:', {
                initialMaxLines,
                textLinesCreated: newImprint.textLines.length
            });
            
            this.imprints.push(newImprint);
            
            // ✅ CRITICAL FIX: Initialize activeMethodSku and methodsData for new imprint
            // This ensures data is saved in methodsData isolation, not just textLines
            // Without this, second imprint data won't be preserved when switching
            const imprintStore = useImprintStore();
            const selectedMethod = imprintStore.selectedImprintMethod;
            const currentMethodSku = selectedMethod?.sku;
            
            console.log('[useNewImprintStore] BEFORE auto-initializing method:', {
                newIndex,
                selectedMethod,
                currentMethodSku,
                typeOfSku: typeof currentMethodSku,
                'newImprint.activeMethodSku (before)': this.imprints[newIndex].activeMethodSku,
                'newImprint.methodsData (before)': Object.keys(this.imprints[newIndex].methodsData || {})
            });
            
            if (currentMethodSku && typeof currentMethodSku === 'string') {
                console.log('[useNewImprintStore] Calling setActiveImprintMethod with:', {
                    newIndex,
                    methodSku: currentMethodSku
                });
                
                // Call setActiveImprintMethod to properly initialize methodsData
                this.setActiveImprintMethod(currentMethodSku, newIndex);
                
                console.log('[useNewImprintStore] AFTER setActiveImprintMethod:', {
                    'newImprint.activeMethodSku (after)': this.imprints[newIndex].activeMethodSku,
                    'newImprint.methodsData (after)': Object.keys(this.imprints[newIndex].methodsData || {}),
                    'newImprint.maxLines (after)': this.imprints[newIndex].maxLines,
                    'newImprint.textLines.length (after)': this.imprints[newIndex].textLines?.length
                });
            } else {
                console.error('[useNewImprintStore] Cannot auto-initialize method - invalid SKU:', {
                    currentMethodSku,
                    typeOfSku: typeof currentMethodSku,
                    selectedMethod
                });
            }

            // Keep currentImprintKey on first imprint (0) to avoid affecting it
            // New imprints are controlled by their own imprintKey prop
            // this.currentImprintKey = newIndex;  // DON'T switch automatically
            
            console.log('[useNewImprintStore] New imprint added:', {
                newIndex,
                totalImprints: this.imprints.length,
                newImprintLocation: newImprint.selectedLocation,
                newImprintMaxLines: newImprint.maxLines,
                newImprintTextLinesCount: newImprint.textLines.length,
                currentImprintKey: this.currentImprintKey
            });
        },

        /**
         * Switch to different imprint
         * @param {number} index
         */
        switchToImprint(index) {
            if (index >= 0 && index < this.imprints.length) {
                this.currentImprintKey = index;
                console.log('[useNewImprintStore] Switched to imprint:', index);
            }
        },

        /**
         * Delete imprint by index
         * @param {number} index
         */
        deleteImprint(index) {
            if (index === 0) {
                console.warn('[useNewImprintStore] Cannot delete first imprint');
                return;
            }
            
            if (index >= this.imprints.length) {
                console.warn('[useNewImprintStore] Invalid imprint index:', index);
                return;
            }
            
            // Remove imprint (all data is inside imprint object)
            this.imprints.splice(index, 1);
            
            // Adjust current key if needed
            if (this.currentImprintKey >= this.imprints.length) {
                this.currentImprintKey = this.imprints.length - 1;
            }
            
            console.log('[useNewImprintStore] Imprint deleted:', index, 'Total imprints:', this.imprints.length);
        },

        /**
         * Transform imprint data to cart payload format (Start New Imprint)
         * Returns imprint_lines and properties objects ready for cart
         * @param {number|null} imprintKey - Optional imprint key (defaults to currentImprintKey)
         * @param {Object} priceData - Price data from useAddToCartStore (includes tier pricing)
         * @returns {Object} { imprint_lines, properties, fees }
         */
        transformToCartPayload(imprintKey = null, priceData = null) {
            const key = imprintKey !== null ? imprintKey : this.currentImprintKey;
            const imprint = this.imprints[key];

            console.log('[transformToCartPayload] key:', key);
            console.log('[transformToCartPayload] imprint:', imprint);

            if (!imprint) {
                console.error('[useNewImprintStore] Invalid imprint key:', key);
                console.error('[useNewImprintStore] Available keys:', Object.keys(this.imprints));
                return null;
            }

            const imprintStore = useImprintStore();
            
            // Filter out empty lines (lines without message or color)
/*
            const filledLines = imprint.textLines.filter(line => {
                if (line.type !== 'message') return true; // Keep image lines
                // Line is filled if it has both message and color
                return line.message && line.message.trim() && line.selectedColor;
            });
*/

            // Keep ONLY fully filled inputs based on CURRENT messageType:
            // - 'text': only message lines with text + color
            // - 'image': only image line with imageData + color
            // - 'text_image': both message lines AND image line
            const filledLines = imprint.textLines.filter(line => {
                const messageType = imprint.messageType || 'text';
                
                if (line.type === 'message') {
                    // Message lines are only valid for 'text' and 'text_image' modes
                    if (messageType === 'image') {
                        return false; // Exclude text lines in Image Only mode
                    }
                    return !!(line.message && line.message.trim() && line.selectedColor);
                }

                if (line.type === 'image') {
                    // Image lines are only valid for 'image' and 'text_image' modes
                    if (messageType === 'text') {
                        return false; // Exclude image lines in Text Only mode
                    }
                    const hasImage = !!imprint.imageData;
                    const hasImageColor = !!(line.selectedColor || line.selectedColorObj);
                    return hasImage && hasImageColor;
                }

                // Any other types (if ever added) -> exclude by default
                return false;
            });
            
            // Build imprint_lines object (only filled lines)
            const imprintLines = {};
            let lineNum = 1;
            
            filledLines.forEach(line => {
                if (line.type === 'message') {
                    imprintLines[`line${lineNum}`] = {
                        type: line.customColor ? 'custom_color' : 'standard_color',
                        color: line.customColor 
                            ? line.selectedColor // Pantone name as-is
                            : (line.selectedColorObj?.file_name?.replace('.png', '') || '')
                    };
                    lineNum++;
                } else if (line.type === 'image') {
                    // FIX: Add line_img for BOTH 'image' AND 'text_image' message types
                    // This ensures backend can count image color for fee calculation
                    if ((imprint.messageType === 'text_image' || imprint.messageType === 'image') && line.selectedColorObj) {
                        imprintLines['line_img'] = {
                            type: 'standard_color',
                            color: line.selectedColorObj.file_name?.replace('.png', '') || ''
                        };
                    }
                }
            });
            
            // Build properties object
            const properties = {
                "Imprint location": imprint.selectedLocation.title || '',
                "_Imprint Location SKU": imprint.selectedLocation.sku || '',
                
                "Imprint Method": imprintStore.selectedImprintMethod?.name || '',
                "_Imprint Method SKU": imprintStore.selectedImprintMethod?.sku || '',
                "_colorUpcharge": imprintStore.colorUpcharge || {},
                "_sizeUpcharge": imprintStore.sizeUpcharge || {}
            };
            
            // Add text lines to properties (only filled lines)
            lineNum = 1;
            filledLines.forEach(line => {
                if (line.type === 'message') {
                    properties[`Line ${lineNum} text`] = line.message || '';
                    properties[`Line ${lineNum} color`] = line.customColor 
                        ? line.selectedColor // Pantone name
                        : (line.selectedColorObj?.file_name?.replace('.png', '') || '');
                    
                    // Add color SKU only for standard colors (not Pantone)
                    if (!line.customColor && line.selectedColorObj?.sku) {
                        properties[`_Imprint Line ${lineNum} Color SKU`] = line.selectedColorObj.sku;
                    }
                    lineNum++;
                }
            });
            
            // Add image data if exists
            if (imprint.imageData) {
                const imageLine = imprint.textLines.find(l => l.type === 'image');
                
                // Always add filename and URL for both 'image' and 'text_image' modes
                properties["Add an Image Filename"] = imprint.imageData.filename || '';
                properties["_Add an Image Url"] = imprint.imageData.url || '';
                
                // Always include image colour if selected (required for order instructions and fee calculation)
                // Applies to both 'image' (Image Only) and 'text_image' (Text & Image) modes
                if (imageLine && imageLine.selectedColorObj) {
                    properties["Add an Image Color"] = imageLine.selectedColorObj.file_name?.replace('.png', '') || '';
                    properties["_Add an Image Color SKU"] = imageLine.selectedColorObj.sku || '';
                }
            }
            
            // Add special instructions
            const selectionStore = useSelectionStore();
            properties["Special Instruction"] = selectionStore.specialInstructions || '';
            
            // Calculate fees using feeCalculator utilities with backend config
            // ✅ FIX: Use getUniqueColorsCount() instead of maxColorsTracking.uniqueColors.length
            // maxColorsTracking only tracks colors with SKU, but fee calculation needs ALL unique colors
            const uniqueColorsCount = getUniqueColorsCount(imprint);
            const uniqueCustomColorsCount = getUniqueCustomColorsCount(imprint);
            
            // CRITICAL: hasImage should check BOTH:
            // 1. Image data exists
            // 2. Message type allows images (image or text_image, NOT text)
            const imageDataExists = !!imprint.imageData;
            const messageType = imprint.messageType || 'text';
            const messageTypeAllowsImage = messageType === 'image' || messageType === 'text_image';
            const hasImage = imageDataExists && messageTypeAllowsImage;
            
            // Check if this is a COMDA store
            const isComda = window.Shopify?.shop?.includes('comda') ?? true;
            
            // Get backend fee configuration
            const sku = imprintStore.selectedImprintMethod?.sku;
            let backendConfig = {};
            
            if (sku && imprintStore.fullJson?.configurable_array?.[sku]) {
                const imprintData = imprintStore.fullJson.configurable_array[sku].configurable_2_data?.imprint_data;
                if (imprintData) {
                    backendConfig = {
                        setup_fee: imprintData.setup_fee || 0,
                        running_fee: imprintData.running_fee || 0,
                        custom_colour_fee: imprintData.custom_colour_fee || 0,
                        image_fee: imprintData.image_fee || 0,
                        proof_fee: imprintData.proof_fee || 0
                    };
                }
            }
            
            console.log('[useNewImprintStore] Backend fee config:', backendConfig);
            
            // Use calculateAllFees utility with backend config
            const fees = calculateAllFees({
                uniqueColorsCount: uniqueColorsCount,
                totalQuantity: 1, // Will be multiplied by actual quantity later
                hasPantoneColor: uniqueCustomColorsCount,
                hasImage: hasImage,
                artworkMethod: 'new',
                isComda: isComda,
                backendConfig: backendConfig  // ✅ Pass backend config for correct rates
            });
            
            // Add fees to properties
            // Note: running_fee is NOT added here - it's handled per-item in cart
            const appStore = useAppStore();
            properties["_price_details"] = {
                tier: priceData?.tier || [],
                b2bTier: appStore.getB2BTierForSku(sku) ?? null,
                tierMSRP: priceData?.tierMSRP || 0,
                priceByQty: priceData?.priceByQty || 0,
                priceSizeUpcharge: priceData?.priceSizeUpcharge || 0,
                priceColorUpcharge: priceData?.priceColorUpcharge || 0,


                setup_fee: fees.setup_fee,           // Uses correct COMDA/NON-COMDA formula
                setup_fee_qty: fees.setup_fee_qty,
                setup_fee_unit_price: fees.setup_fee_unit_price,
                proof_fee: fees.proof_fee,           // $5 standard
                proof_fee_unit_price: fees.proof_fee_unit_price,
                image_fee: fees.image_fee,           // $25 if has image
                image_fee_qty: fees.image_fee_qty,
                image_fee_unit_price: fees.image_fee_unit_price,
                customColourFee: fees.customColourFee, // $50 if Pantone
                customColourFee_qty: fees.customColourFee_qty,
                customColourFee_unit_price: fees.customColourFee_unit_price,
                running_fee_multiplier: fees.running_fee_multiplier,
                running_fee_rate: fees.running_fee_rate,
                running_fee: 0  // Running fee is NOT in _price_details (it's per-item)
            };
            
            console.log('[useNewImprintStore] Transform to cart payload:', {
                imprintKey: key,
                totalLines: imprint.textLines.length,
                filledLines: filledLines.length,
                uniqueColorsCount,
                uniqueCustomColorsCount,
                imageDataExists,
                messageType,
                messageTypeAllowsImage,
                hasImage,
                isComda,
                fees: fees
            });
            
            return {
                imprint_lines: imprintLines,
                properties: properties,
                fees: fees  // Return fees object for use by addToCart
            };
        },

        /**
         * Get cart properties for all imprints (for multiple imprints)
         * @returns {Array} Array of { imprint_lines, properties } objects
         */
        getAllImprintsCartPayload() {
            const payloads = [];
            
            for (let i = 0; i < this.imprints.length; i++) {
                const payload = this.transformToCartPayload(i);
                if (payload) {
                    payloads.push({
                        imprintIndex: i,
                        ...payload
                    });
                }
            }
            
            console.log('[useNewImprintStore] All imprints payload:', payloads.length, 'imprints');
            return payloads;
        },

        // ====================================
        // SESSION STORAGE PERSISTENCE
        // ====================================

        /**
         * Save imprints state to sessionStorage
         * Called before redirect to preserve user data
         */
        saveToSessionStorage() {
            try {
                const data = {
                    version: STORAGE_VERSION,
                    timestamp: Date.now(),
                    productKey: window.location.pathname,  // ✅ NEW: Save product pathname for validation
                    currentImprintKey: this.currentImprintKey,
                    imprints: this.imprints.map(imprint => ({
                        // Location
                        selectedLocation: imprint.selectedLocation,
                        
                        // Methods data (CRITICAL - preserves all method states)
                        methodsData: imprint.methodsData,
                        activeMethodSku: imprint.activeMethodSku,
                        
                        // ✅ NEW: Locations data (preserves textLines per location)
                        locationsData: imprint.locationsData,
                        
                        // Legacy fields (for backwards compatibility)
                        messageType: imprint.messageType,
                        textLines: imprint.textLines,
                        imageData: imprint.imageData,
                        specialInstructions: imprint.specialInstructions,
                        maxLines: imprint.maxLines,
                        
                        // Apply to All state
                        applyToAll: imprint.applyToAll,
                        
                        // Max colors tracking
                        maxColorsTracking: imprint.maxColorsTracking,
                        
                        // Saved Pantone colors
                        savedPantoneColors: imprint.savedPantoneColors,
                        
                        // Saved image line data
                        savedImageLineData: imprint.savedImageLineData
                    }))
                };
                
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
                console.log('[useNewImprintStore] 💾 State saved to sessionStorage:', {
                    imprints: data.imprints.length,
                    currentKey: data.currentImprintKey
                });
                
                return true;
            } catch (error) {
                console.error('[useNewImprintStore] ❌ Failed to save to sessionStorage:', error);
                return false;
            }
        },

        /**
         * Restore imprints state from sessionStorage
         * Called on page load to recover user data after redirect
         */
        restoreFromSessionStorage() {
            try {
                const raw = sessionStorage.getItem(STORAGE_KEY);
                if (!raw) {
                    console.log('[useNewImprintStore] No saved state found in sessionStorage');
                    return false;
                }
                
                const data = JSON.parse(raw);
                
                // Version check
                if (data.version !== STORAGE_VERSION) {
                    console.warn('[useNewImprintStore] ⚠️ Storage version mismatch');
                    this.clearSessionStorage();
                    return false;
                }
                
                // ✅ CRITICAL: Product key check (prevent restoring data from different product)
                const currentProductKey = window.location.pathname;
                if (data.productKey && data.productKey !== currentProductKey) {
                    console.warn('[useNewImprintStore] ⚠️ Product key mismatch:', {
                        stored: data.productKey,
                        current: currentProductKey
                    });
                    console.log('[useNewImprintStore] Clearing storage from different product');
                    this.clearSessionStorage();
                    return false;
                }
                
                // TTL check (30 minutes)
                const age = Date.now() - data.timestamp;
                if (age > STORAGE_TTL) {
                    console.warn('[useNewImprintStore] ⚠️ Storage data expired (age:', Math.round(age / 1000), 'seconds)');
                    this.clearSessionStorage();
                    return false;
                }
                
                // Restore current imprint key
                this.currentImprintKey = data.currentImprintKey;
                
                // Restore each imprint
                this.imprints = data.imprints.map((savedImprint) => {
                    const imprint = {
                        // Location
                        selectedLocation: savedImprint.selectedLocation || {
                            sku: null,
                            title: null,
                            imprintLines: 5
                        },
                        
                        // Methods data (CRITICAL!)
                        methodsData: savedImprint.methodsData || {},
                        activeMethodSku: savedImprint.activeMethodSku || null,
                        
                        // ✅ NEW: Locations data (CRITICAL!)
                        locationsData: savedImprint.locationsData || {},
                        
                        // Legacy fields
                        messageType: savedImprint.messageType || 'text',
                        textLines: savedImprint.textLines || [],
                        imageData: savedImprint.imageData || null,
                        specialInstructions: savedImprint.specialInstructions || '',
                        maxLines: savedImprint.maxLines || 5,
                        
                        // Apply to All
                        applyToAll: savedImprint.applyToAll || {
                            active: false,
                            sourceLineId: null,
                            sourceLineRandID: null,
                            color: null,
                            colorObj: null,
                            customColor: false
                        },
                        
                        // Max colors tracking
                        maxColorsTracking: savedImprint.maxColorsTracking || {
                            uniqueColors: [],
                            maxColors: null,
                            isMaxReached: false
                        },
                        
                        // Saved Pantone colors
                        savedPantoneColors: savedImprint.savedPantoneColors || [],
                        
                        // Saved image line data
                        savedImageLineData: savedImprint.savedImageLineData || null,
                        
                        // Fresh state (not saved)
                        errors: {},
                        nextLineId: Math.max(...(savedImprint.textLines || []).map(l => l.id || 0), 0) + 1,
                        customPantoneInputs: {},
                        pendingColorSelection: {
                            lineId: null,
                            color: null,
                            colorObj: null,
                            customColor: false
                        }
                    };
                    
                    // Ensure textLines have randID (for Vue key tracking)
                    imprint.textLines = imprint.textLines.map(line => ({
                        ...line,
                        randID: line.randID || (Date.now() + Math.random() * 1000),
                        showColorPicker: false
                    }));
                    
                    return imprint;
                });
                
                console.log('[useNewImprintStore] ✅ State restored from sessionStorage:', {
                    imprints: this.imprints.length,
                    currentKey: this.currentImprintKey,
                    age: Math.round(age / 1000) + 's ago'
                });
                
                return true;
            } catch (error) {
                console.error('[useNewImprintStore] ❌ Failed to restore from sessionStorage:', error);
                this.clearSessionStorage();
                return false;
            }
        },

        /**
         * Clear saved state from sessionStorage
         */
        clearSessionStorage() {
            try {
                sessionStorage.removeItem(STORAGE_KEY);
                console.log('[useNewImprintStore] 🗑️ SessionStorage cleared');
            } catch (error) {
                console.error('[useNewImprintStore] ❌ Failed to clear sessionStorage:', error);
            }
        }
    }
});
